import React, { useCallback, useEffect, useState } from 'react';
import { ScheduleReturn } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import { Text } from '../common/Catalyst/text';
import { Subheading } from '../common/Catalyst/heading';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';
import { dueRule, frequency, scheduleStatus } from './enumLabels';
import ScheduleDialog from './ScheduleDialog';

interface Props {
  hoaId: string;
  canManage: boolean;
}

/** How a schedule's due date is worked out, in one phrase. */
const dueDescription = (s: ScheduleReturn): string => {
  const base =
    s.due_rule === 'day_of_month'
      ? `day ${s.due_day ?? '?'} of the period`
      : dueRule.label(s.due_rule).toLowerCase();
  if (!s.due_offset_days) return base;
  const days = Math.abs(s.due_offset_days);
  const dir = s.due_offset_days < 0 ? 'before' : 'after';
  return `${base}, ${days} day${days === 1 ? '' : 's'} ${dir}`;
};

const ScheduleList: React.FC<Props> = ({ hoaId, canManage }) => {
  const [schedules, setSchedules] = useState<ScheduleReturn[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleReturn | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await billingService.listSchedules({ hoaId });
      setSchedules(data.schedules);
    } catch (err) {
      console.error('Error loading schedules:', err);
      setError(billingErrorMessage(err, "Couldn't load the dues schedules."));
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openNew = (): void => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (s: ScheduleReturn): void => {
    setEditing(s);
    setDialogOpen(true);
  };

  if (loading) return <LoadingSpinner />;

  const active = (schedules ?? []).filter((s) => s.status === 'active');

  return (
    <>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load the dues schedules"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline
          />
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4">
        <div>
          <Subheading>Dues schedules</Subheading>
          <Text className="mt-1">
            What this association bills and how often. A schedule writes nothing
            on its own — charges appear when a billing run is generated and
            posted.
          </Text>
        </div>
        {canManage && (
          <Button color="sky" onClick={openNew}>
            New schedule
          </Button>
        )}
      </div>

      {active.length > 1 && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="More than one schedule is active"
            alertMessage={
              'Dues and the current period charge read the earliest active ' +
              'schedule, so the others are not billing anything. End the ' +
              'ones that should not apply.'
            }
            isVisible
            onClose={() => undefined}
            timeout={0}
            isInline
          />
        </div>
      )}

      {(schedules ?? []).length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>
            This association has no dues schedule, so nothing recurring is
            billed. That is not the same as billing zero — until a schedule
            exists and is active, a billing run has nothing to generate.
          </Text>
        </div>
      ) : (
        <Table dense>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Amount</TableHeader>
              <TableHeader>Every</TableHeader>
              <TableHeader>Periods anchored to</TableHeader>
              <TableHeader>Due</TableHeader>
              <TableHeader>Ends</TableHeader>
              <TableHeader>Status</TableHeader>
              {canManage && <TableHeader className="text-right"> </TableHeader>}
            </TableRow>
          </TableHead>
          <TableBody>
            {(schedules ?? []).map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>{formatMoney(s.amount)}</TableCell>
                <TableCell>{frequency.label(s.frequency)}</TableCell>
                <TableCell>{formatBillingDate(s.first_period_start)}</TableCell>
                <TableCell>{dueDescription(s)}</TableCell>
                <TableCell>
                  {s.effective_to ? formatBillingDate(s.effective_to) : '—'}
                </TableCell>
                <TableCell>
                  <Badge color={scheduleStatus.color(s.status)}>
                    {scheduleStatus.label(s.status)}
                  </Badge>
                </TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    <Button plain onClick={() => openEdit(s)}>
                      Edit
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ScheduleDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
        hoaId={hoaId}
        schedule={editing}
      />
    </>
  );
};

export default ScheduleList;
